import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';

const ROLE_LABELS = {
  guide: 'Guide',
  customer: 'Adventure Seeker',
};

const SOURCE_LABELS = {
  waitlist: 'Waitlist',
  contact_form: 'Contact Form',
  newsletter: 'Newsletter',
  referral: 'Referral',
  partner: 'Partner',
  event: 'Event',
  other: 'Other',
};

function useMarkets() {
  const [markets, setMarkets] = useState([]);
  useEffect(() => {
    apiRequest('/api/v1/admin/markets?limit=100')
      .then((res) => {
        if (res.success) {
          setMarkets(res.data || []);
        }
      })
      .catch(() => {});
  }, []);
  return markets;
}

function FilterBar({ filters, onFilterChange, markets }) {
  const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' };
  return (
    <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
      <select
        value={filters.interested_role || ''}
        onChange={(e) => onFilterChange({ ...filters, interested_role: e.target.value || undefined })}
        style={inputStyle}
      >
        <option value="">All Types</option>
        <option value="guide">Guide</option>
        <option value="customer">Adventure Seeker</option>
      </select>

      <select
        value={filters.hubspot_synced === undefined ? '' : String(filters.hubspot_synced)}
        onChange={(e) => {
          const val = e.target.value;
          onFilterChange({
            ...filters,
            hubspot_synced: val === '' ? undefined : val === 'true',
          });
        }}
        style={inputStyle}
      >
        <option value="">HubSpot: All</option>
        <option value="true">Synced</option>
        <option value="false">Not Synced</option>
      </select>

      <select
        value={filters.market_id || ''}
        onChange={(e) => onFilterChange({ ...filters, market_id: e.target.value || undefined })}
        style={inputStyle}
      >
        <option value="">All Markets</option>
        {markets.map((m) => (
          <option key={m.id} value={m.id}>{m.name}</option>
        ))}
      </select>

      <select
        value={filters.email_confirmed === undefined ? '' : String(filters.email_confirmed)}
        onChange={(e) => {
          const val = e.target.value;
          onFilterChange({
            ...filters,
            email_confirmed: val === '' ? undefined : val === 'true',
          });
        }}
        style={inputStyle}
      >
        <option value="">Email: All</option>
        <option value="true">Confirmed</option>
        <option value="false">Not Confirmed</option>
      </select>

      <input
        type="date"
        value={filters.from || ''}
        onChange={(e) => onFilterChange({ ...filters, from: e.target.value || undefined })}
        style={inputStyle}
        aria-label="From date"
      />
      <input
        type="date"
        value={filters.to || ''}
        onChange={(e) => onFilterChange({ ...filters, to: e.target.value || undefined })}
        style={inputStyle}
        aria-label="To date"
      />
    </div>
  );
}

export default function LeadManagement() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [syncCooldown, setSyncCooldown] = useState(false);
  const markets = useMarkets();

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (filters.interested_role) {
        params.set('interested_role', filters.interested_role);
      }
      if (filters.hubspot_synced !== undefined) {
        params.set('hubspot_synced', String(filters.hubspot_synced));
      }
      if (filters.market_id) {
        params.set('market_id', filters.market_id);
      }
      if (filters.email_confirmed !== undefined) {
        params.set('email_confirmed', String(filters.email_confirmed));
      }
      if (filters.from) {
        params.set('from', filters.from);
      }
      if (filters.to) {
        params.set('to', filters.to);
      }

      const response = await apiRequest(`/api/v1/admin/leads?${params.toString()}`);
      if (response.success) {
        setData(response.data);
        setTotal(response.total);
        setHasMore(response.has_more);
        setError(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
    setSelected(new Set());
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === data.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.map((r) => r.id)));
    }
  };

  const unsyncedSelected = data.filter((r) => selected.has(r.id) && !r.hubspot_contact_id);

  const handleSyncSelected = async () => {
    if (unsyncedSelected.length === 0) { return; }
    setSyncCooldown(true);
    try {
      const result = await apiRequest('/api/v1/admin/leads/sync', {
        method: 'POST',
        body: JSON.stringify({ leadIds: unsyncedSelected.map((r) => r.id) }),
        headers: { 'Content-Type': 'application/json' },
      });
      setToast(`Enqueued ${result.enqueued} for sync. ${result.skipped} already synced (skipped).`);
      setTimeout(() => setToast(null), 5000);
      setSelected(new Set());
      fetchData();
    } catch (err) {
      setToast(`Sync failed: ${err.message}`);
      setTimeout(() => setToast(null), 5000);
    }
    setTimeout(() => setSyncCooldown(false), 60000);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="leads"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null,
      }}
    >
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Leads</h1>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {selected.size > 0 && (
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                {selected.size} lead{selected.size !== 1 ? 's' : ''} selected
              </span>
            )}
            <button
              disabled={unsyncedSelected.length === 0 || syncCooldown}
              onClick={handleSyncSelected}
              style={{
                padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                border: 'none', cursor: unsyncedSelected.length === 0 || syncCooldown ? 'not-allowed' : 'pointer',
                opacity: unsyncedSelected.length === 0 || syncCooldown ? 0.5 : 1,
                backgroundColor: '#3b82f6', color: '#fff',
              }}
            >
              Sync Selected
            </button>
          </div>
        </div>

        {toast && (
          <div style={{
            position: 'fixed', top: '16px', right: '16px', padding: '12px 20px',
            borderRadius: '8px',
            backgroundColor: toast.startsWith('Sync failed') ? '#fee2e2' : '#d1fae5',
            color: toast.startsWith('Sync failed') ? '#dc2626' : '#065f46',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, fontSize: '14px',
          }}>
            {toast}
          </div>
        )}

        <FilterBar filters={filters} onFilterChange={handleFilterChange} markets={markets} />

        {error && (
          <div style={{ padding: '16px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {loading && data.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
        ) : data.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>No leads found.</div>
        ) : (
          <>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
              {total} total — page {page} of {totalPages}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '8px', width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={data.length > 0 && selected.size === data.length}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th style={{ padding: '8px' }}>Email</th>
                  <th style={{ padding: '8px' }}>Lead Type</th>
                  <th style={{ padding: '8px' }}>Source</th>
                  <th style={{ padding: '8px' }}>Market</th>
                  <th style={{ padding: '8px' }}>Campaign</th>
                  <th style={{ padding: '8px' }}>Email Confirmed</th>
                  <th style={{ padding: '8px' }}>HubSpot</th>
                  <th style={{ padding: '8px' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={() => toggleSelect(row.id)}
                        aria-label={`Select ${row.email}`}
                      />
                    </td>
                    <td style={{ padding: '8px', fontSize: '13px' }}>{row.email}</td>
                    <td style={{ padding: '8px', fontSize: '13px' }}>
                      {ROLE_LABELS[row.interested_role] || '—'}
                    </td>
                    <td style={{ padding: '8px', fontSize: '13px' }}>
                      {SOURCE_LABELS[row.source] || row.source || '—'}
                    </td>
                    <td style={{ padding: '8px', fontSize: '13px' }}>{row.market_name || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '13px' }}>{row.campaign_name || '—'}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      {row.email_confirmed ? '✅' : '❌'}
                    </td>
                    <td style={{ padding: '8px' }}>
                      {row.hubspot_contact_id ? (
                        <a
                          href={`https://app.hubspot.com/contacts/244413567/contact/${row.hubspot_contact_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#065f46', textDecoration: 'none', fontSize: '13px' }}
                        >
                          {'✅'} Synced
                        </a>
                      ) : (
                        <span style={{ color: '#dc2626', fontSize: '13px' }}>{'❌'} Not synced</span>
                      )}
                    </td>
                    <td style={{ padding: '8px', fontSize: '12px', color: '#6b7280' }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center' }}>
              <button
                disabled={page <= 1}
                onClick={() => { setPage(page - 1); setSelected(new Set()); }}
                style={{
                  padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db',
                  cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1,
                }}
              >
                Prev
              </button>
              <span style={{ padding: '6px 12px', fontSize: '13px', color: '#6b7280' }}>
                {page} / {totalPages}
              </span>
              <button
                disabled={!hasMore}
                onClick={() => { setPage(page + 1); setSelected(new Set()); }}
                style={{
                  padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db',
                  cursor: !hasMore ? 'not-allowed' : 'pointer', opacity: !hasMore ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
